import { useState, useCallback, useMemo } from 'react';
import { SearchItem, SearchResults } from '../../../types';

interface Props {
	searchResults: SearchResults;
	isFetchingAssets: boolean;
	showDropdownResults: boolean;
	handleEnterKey: () => void;
	setShowDropdownResults: (show: boolean) => void;
	setSearchTerm: (term: string) => void;
}

export const useSearchItemKeyboardNavigation = ({
	searchResults,
	isFetchingAssets,
	showDropdownResults,
	handleEnterKey,
	setShowDropdownResults,
	setSearchTerm,
}: Props) => {
	const [currentActiveSearchItem, setCurrentActiveSearchItem] = useState<SearchItem | undefined>(
		undefined,
	);

	const allItemsFromResults = useMemo(() => {
		if (!searchResults) {
			return [];
		}

		return Object.entries(searchResults)
			.filter(([key]) => key !== 'metaproperties' && key !== 'tags')
			.flatMap(([, value]) => {
				if (value && 'items' in value) {
					return value.items as SearchItem[];
				}

				return [];
			});
	}, [searchResults]);

	const handleArrowKeyNavigation = useCallback(
		(direction: 'up' | 'down') => {
			if (allItemsFromResults.length === 0) {
				return;
			}

			let nextIndex: number;

			if (currentActiveSearchItem === undefined) {
				nextIndex = 0;
			} else {
				const currentIndex = allItemsFromResults.findIndex(
					(item) => item.id === currentActiveSearchItem.id,
				);

				if (direction === 'down') {
					nextIndex =
						currentIndex === -1
							? 0
							: Math.min(currentIndex + 1, allItemsFromResults.length - 1);
				} else {
					nextIndex =
						currentIndex === -1
							? allItemsFromResults.length - 1
							: Math.max(currentIndex - 1, 0);
				}
			}

			setCurrentActiveSearchItem(allItemsFromResults[nextIndex]);
		},
		[allItemsFromResults, currentActiveSearchItem],
	);

	const onInputKeyUp = useCallback(
		(e: React.KeyboardEvent) => {
			e.stopPropagation();
			e.preventDefault();

			if (!showDropdownResults) {
				setCurrentActiveSearchItem(undefined);
			}

			if (e.key === 'Enter') {
				if (isFetchingAssets) {
					// We block new asset fetch requests while one is happening to prevent race conditions
					return;
				}

				handleEnterKey();

				return;
			}

			switch (e.key) {
				case 'ArrowDown': {
					handleArrowKeyNavigation('down');
					break;
				}

				case 'ArrowUp': {
					handleArrowKeyNavigation('up');
					break;
				}

				case 'Escape':
					setShowDropdownResults(false);
					setCurrentActiveSearchItem(undefined);
					setSearchTerm('');
					break;
			}
		},
		[
			handleArrowKeyNavigation,
			handleEnterKey,
			isFetchingAssets,
			setSearchTerm,
			setShowDropdownResults,
			showDropdownResults,
		],
	);

	return {
		currentActiveSearchItem,
		onInputKeyUp,
		setCurrentActiveSearchItem,
	};
};
